import { useEffect, useState } from 'react'
import { Trans } from 'react-i18next'

import {
  ConditionBooleanOperator,
  ConditionExpression,
  ConditionFeatureFlag,
  ConditionTextOperator,
  ConditionValue,
} from '@makyoapp/makyo-common'
import { Eye24, EyeSlash24 } from '@makyoapp/makyo-icon/icon'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'

import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { ComponentSettingKey } from '@/shared/builder/types'
import { getCurrentFormFieldColumns } from '@/shared/form-utils'
import { filterComponentInBody } from '@/utils/builder/app-builder'

import { ConditionRepeaterV3 } from './ConditionRepeaterV3'

export type VisibilityMode = 'always-visible' | 'show-only-when' | 'always-hidden'

interface VisibilitySelectorProps {
  id?: string
  value: ConditionValue | null | undefined
  onChange?: (nextValue: ConditionValue | null) => void
  featureFlags?: ConditionFeatureFlag
  parentFormId?: string
}

/**
 * VisibilitySelector - Component for selecting visibility mode with 3 options:
 * 1. Always visible - Always visible (value = null or conditions = [])
 * 2. Show only when - Show only when conditions are met (value with conditions)
 * 3. Always hidden - Always hidden (value with special conditions that always evaluate to false)
 */
export function VisibilitySelector({ id, value, onChange, featureFlags, parentFormId }: VisibilitySelectorProps) {
  const { tables } = useDbClientContext()
  const { activeLayerClient } = useActiveLayerContext()

  const getDefaultCondition = (): ConditionExpression[] => {
    // form elements: generate default form condition
    if (parentFormId) {
      const containerSettings = activeLayerClient?.settings._BodySettings
      const form = activeLayerClient?.componentsMap.get(parentFormId as string)

      if (form) {
        const formChildren = filterComponentInBody(form._Body, (el) => ['form', 'upload'].includes(el._GroupID))
        const formSettings = form?.settings
        const dataSource = formSettings?.[ComponentSettingKey.FormDataSource]
        const table = tables.find((t) => t.id === dataSource)

        if (containerSettings && formChildren && table) {
          const columns = getCurrentFormFieldColumns(containerSettings, formChildren)
          const firstColId = columns[0]

          return [
            {
              lhs: { flag: ConditionFeatureFlag.FormData, formId: parentFormId, col: firstColId },
              operator: ConditionTextOperator.IsNotEmpty,
            },
          ]
        }
      }
    }

    return [
      {
        lhs: { flag: ConditionFeatureFlag.UserLoggedIn },
        operator: ConditionBooleanOperator.IsFalse,
      },
    ]
  }

  // Helper function to check if the condition is always-hidden pattern
  // Note: We check the pattern regardless of isAny value, as it might be changed by parent
  const isAlwaysHiddenPattern = (val: ConditionValue | null | undefined): boolean => {
    if (!val || !val.conditions) return false
    const matchesPattern =
      val.conditions.length === 2 &&
      val.conditions[0]?.lhs?.flag === ConditionFeatureFlag.UserLoggedIn &&
      val.conditions[1]?.lhs?.flag === ConditionFeatureFlag.UserLoggedIn &&
      val.conditions[0]?.operator === ConditionBooleanOperator.IsFalse &&
      val.conditions[1]?.operator === ConditionBooleanOperator.IsTrue

    // Pattern matches if conditions match, regardless of isAny
    // But we prefer isAny === false for strict always-hidden
    return matchesPattern
  }

  const getCurrentMode = (): VisibilityMode => {
    if (!value || value === null || value === undefined) {
      return 'always-visible'
    }

    // If value exists but has no conditions, we need to check if it's transitioning
    // For now, we'll check if it's "always hidden" first
    if (!value.conditions || value.conditions.length === 0) {
      // Empty conditions could be "always-visible" or transitioning to "show-only-when"
      // We'll use a flag: if value exists as object (not null), it might be "show-only-when"
      // But to be safe, default to "always-visible" unless we have a way to distinguish
      return 'always-visible'
    }

    // Check if it's "always hidden" mode
    // We detect it by checking if conditions have a specific pattern that always evaluates to false
    // Using a condition that checks UserLoggedIn is false AND true (impossible condition)
    const isAlwaysHidden =
      value.conditions.length === 2 &&
      value.conditions[0]?.lhs?.flag === ConditionFeatureFlag.UserLoggedIn &&
      value.conditions[1]?.lhs?.flag === ConditionFeatureFlag.UserLoggedIn &&
      value.conditions[0]?.operator === ConditionBooleanOperator.IsFalse &&
      value.conditions[1]?.operator === ConditionBooleanOperator.IsTrue &&
      value.isAny === false

    if (isAlwaysHidden) {
      return 'always-hidden'
    }

    return 'show-only-when'
  }

  const [mode, setMode] = useState<VisibilityMode>(getCurrentMode())
  const [showConditions, setShowConditions] = useState<boolean>(() => {
    // Show conditions if there are existing conditions
    return !!(value && value.conditions && value.conditions.length > 0)
  })

  // State to save the last condition from "show-only-when" mode only
  // Do not save condition from always-hidden
  const [lastShowOnlyWhenConditions, setLastShowOnlyWhenConditions] = useState<ConditionValue | null>(() => {
    // Save the initial condition if it exists and is not always-hidden pattern
    if (value && value.conditions && value.conditions.length > 0 && !isAlwaysHiddenPattern(value)) {
      return value
    }
    return null
  })

  // Sync mode when value changes externally
  useEffect(() => {
    // If value is null/undefined, definitely "always-visible"
    if (!value || value === null || value === undefined) {
      if (mode !== 'always-visible') {
        setMode('always-visible')
      }
      setShowConditions(false)
      return
    }

    // Check if it's "always-hidden" mode (has specific pattern)
    if (isAlwaysHiddenPattern(value)) {
      if (mode !== 'always-hidden') {
        setMode('always-hidden')
      }
      setShowConditions(false)
      return
    }

    // If value exists as object (not null), it could be "show-only-when" mode
    // If it has conditions, definitely "show-only-when"
    if (value.conditions && value.conditions.length > 0) {
      // Don't override if mode is already "always-hidden" (user just clicked it, waiting for value to update)
      // Also don't override if mode is "show-only-when" and value is always-hidden pattern (transitioning)
      if (mode === 'show-only-when' && isAlwaysHiddenPattern(value)) {
        setShowConditions(false)
        return
      }

      if (mode !== 'show-only-when' && mode !== 'always-hidden') {
        setMode('show-only-when')
      }
      // Only show conditions if mode is "show-only-when" and not always-hidden pattern
      if (mode === 'show-only-when' && !isAlwaysHiddenPattern(value)) {
        setShowConditions(true)
      }
      // Save condition to lastShowOnlyWhenConditions only if it is not always-hidden pattern
      if (!isAlwaysHiddenPattern(value)) {
        setLastShowOnlyWhenConditions(value)
      }
      return
    }

    // If value exists as object but conditions are empty:
    // - If current mode is "show-only-when", keep it (user just switched from "always-visible")
    // - Otherwise, it might be transitioning, so keep current mode
    // This allows "show-only-when" mode to persist when conditions array is empty
    if (value && typeof value === 'object' && (!value.conditions || value.conditions.length === 0)) {
      // Keep current mode - don't override if it's already "show-only-when" mode
      // This handles the transition from "always-visible" to "show-only-when"
      if (mode === 'show-only-when') {
        // Keep show-only-when mode, but don't show conditions yet
        setShowConditions(false)
      }
      // If mode is not "show-only-when", it might be transitioning, so don't change it
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const handleModeChange = (newMode: VisibilityMode) => {
    if (newMode === 'always-visible') {
      // Before changing to null, save the current condition if it exists and is not always-hidden pattern
      if (value && value.conditions && value.conditions.length > 0 && !isAlwaysHiddenPattern(value)) {
        setLastShowOnlyWhenConditions(value)
      }
      setMode(newMode)
      setShowConditions(false)
      onChange?.(null)
    } else if (newMode === 'always-hidden') {
      // Before switching to always-hidden, save current show-only-when conditions if valid
      if (value && value.conditions && value.conditions.length > 0 && !isAlwaysHiddenPattern(value)) {
        setLastShowOnlyWhenConditions(value)
      }

      setMode(newMode)
      setShowConditions(false)
      onChange?.({
        isAny: false,
        conditions: [
          {
            lhs: { flag: ConditionFeatureFlag.UserLoggedIn },
            operator: ConditionBooleanOperator.IsFalse,
          },
          {
            lhs: { flag: ConditionFeatureFlag.UserLoggedIn },
            operator: ConditionBooleanOperator.IsTrue,
          },
        ],
      })
    } else if (newMode === 'show-only-when') {
      setMode(newMode)
      const isCurrentAlwaysHidden = isAlwaysHiddenPattern(value)
      const isComingFromAlwaysHidden = mode === 'always-hidden'

      if (!value || value === null || value === undefined || isCurrentAlwaysHidden) {
        // If switching from always-hidden, restore lastShowOnlyWhenConditions if it exists and is valid
        // Otherwise, if not coming from always-hidden, restore if valid
        const shouldRestoreLastConditions =
          lastShowOnlyWhenConditions &&
          lastShowOnlyWhenConditions.conditions &&
          lastShowOnlyWhenConditions.conditions.length > 0 &&
          !isAlwaysHiddenPattern(lastShowOnlyWhenConditions)

        if (shouldRestoreLastConditions) {
          setShowConditions(true)
          onChange?.(lastShowOnlyWhenConditions)
        } else {
          setShowConditions(false)
          onChange?.({
            isAny: false,
            conditions: [],
          })
        }
      } else if (!value.conditions || value.conditions.length === 0) {
        // Check if lastShowOnlyWhenConditions is valid (not always-hidden pattern)
        // Restore if valid, regardless of where we're coming from
        const shouldRestoreLastConditions =
          lastShowOnlyWhenConditions &&
          lastShowOnlyWhenConditions.conditions &&
          lastShowOnlyWhenConditions.conditions.length > 0 &&
          !isAlwaysHiddenPattern(lastShowOnlyWhenConditions)

        if (shouldRestoreLastConditions) {
          setShowConditions(true)
          onChange?.(lastShowOnlyWhenConditions)
        } else {
          setShowConditions(false)
          onChange?.({
            isAny: false,
            conditions: [],
          })
        }
      } else {
        // Check if value matches always-hidden pattern (even if isAny is true)
        const matchesAlwaysHiddenPattern =
          value.conditions.length === 2 &&
          value.conditions[0]?.lhs?.flag === ConditionFeatureFlag.UserLoggedIn &&
          value.conditions[1]?.lhs?.flag === ConditionFeatureFlag.UserLoggedIn &&
          value.conditions[0]?.operator === ConditionBooleanOperator.IsFalse &&
          value.conditions[1]?.operator === ConditionBooleanOperator.IsTrue

        // If value matches always-hidden pattern, clear it
        if (matchesAlwaysHiddenPattern) {
          setShowConditions(false)
          // If coming from always-hidden and we have saved conditions, restore them
          if (
            isComingFromAlwaysHidden &&
            lastShowOnlyWhenConditions &&
            lastShowOnlyWhenConditions.conditions &&
            lastShowOnlyWhenConditions.conditions.length > 0 &&
            !isAlwaysHiddenPattern(lastShowOnlyWhenConditions)
          ) {
            setShowConditions(true)
            onChange?.(lastShowOnlyWhenConditions)
          } else {
            onChange?.({
              isAny: false,
              conditions: [],
            })
          }
        } else if (isComingFromAlwaysHidden) {
          // Coming from always-hidden but value doesn't match pattern
          // Restore lastShowOnlyWhenConditions if available
          if (
            lastShowOnlyWhenConditions &&
            lastShowOnlyWhenConditions.conditions &&
            lastShowOnlyWhenConditions.conditions.length > 0 &&
            !isAlwaysHiddenPattern(lastShowOnlyWhenConditions)
          ) {
            setShowConditions(true)
            onChange?.(lastShowOnlyWhenConditions)
          } else {
            setShowConditions(false)
            onChange?.({
              isAny: false,
              conditions: [],
            })
          }
        } else {
          setShowConditions(true)
          // Only save to lastShowOnlyWhenConditions if it's not always-hidden pattern
          if (!isAlwaysHiddenPattern(value) && !matchesAlwaysHiddenPattern) {
            setLastShowOnlyWhenConditions(value)
          }
        }
      }
    }
  }

  const currentMode = mode

  return (
    <div id={id} className='flex flex-col gap-4 w-full'>
      {/* Description */}
      <p className='text-sm text-gray-600 dark:text-gray-400'>
        <Trans>
          Control when this component is shown to users. Choose to make it always visible or set conditions that
          determine its visibility.
        </Trans>
      </p>

      {/* Visibility Mode Options */}
      <div className='flex flex-col gap-2'>
        {/* Always Visible */}
        <button
          onClick={() => handleModeChange('always-visible')}
          className={`flex items-center gap-3 w-full p-4 rounded-lg border-2 transition-all ${
            currentMode === 'always-visible'
              ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
              : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'
          }`}
        >
          <div
            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
              currentMode === 'always-visible' ? 'border-teal-500 bg-teal-500' : 'border-gray-300 dark:border-gray-600'
            }`}
          >
            {currentMode === 'always-visible' && <div className='w-2 h-2 rounded-full bg-white' />}
          </div>
          <div
            className={`flex-shrink-0 ${
              currentMode === 'always-visible' ? 'text-teal-500' : 'text-gray-400 dark:text-gray-500'
            }`}
          >
            <Eye24 width={20} height={20} />
          </div>
          <span
            className={`font-semibold flex-1 text-left ${
              currentMode === 'always-visible' ? 'text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'
            }`}
          >
            <Trans>Always visible</Trans>
          </span>
        </button>

        {/* Show Only When */}
        <div
          className={`rounded-lg border-2 transition-all ${
            currentMode === 'show-only-when'
              ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
              : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
          }`}
        >
          <button onClick={() => handleModeChange('show-only-when')} className='flex items-center gap-3 w-full p-4'>
            <div
              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                currentMode === 'show-only-when'
                  ? 'border-teal-500 bg-teal-500'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
            >
              {currentMode === 'show-only-when' && <div className='w-2 h-2 rounded-full bg-white' />}
            </div>
            <div
              className={`flex-shrink-0 ${
                currentMode === 'show-only-when' ? 'text-teal-500' : 'text-gray-400 dark:text-gray-500'
              }`}
            >
              <MaterialIcon icon='tune' size={20} />
            </div>
            <span
              className={`font-semibold flex-1 text-left ${
                currentMode === 'show-only-when' ? 'text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'
              }`}
            >
              <Trans>Show only when</Trans>
            </span>
          </button>

          {/* Condition Section - Only show when "Show only when" is selected */}
          {currentMode === 'show-only-when' && (
            <div className='px-4 pb-4'>
              {/* Description Text */}
              {!showConditions && (
                <p className='text-sm text-gray-600 dark:text-gray-400 mb-4'>
                  <Trans>
                    Add conditions below to control when this content is visible. Combine multiple conditions for
                    advanced control.
                  </Trans>
                </p>
              )}

              {/* Condition Repeater - Show if conditions exist or if user clicked "Add condition" */}
              {showConditions ? (
                <ConditionRepeaterV3
                  id={id ? `${id}-conditions` : undefined}
                  featureFlags={featureFlags}
                  value={
                    value && value.conditions && value.conditions.length > 0
                      ? value
                      : {
                          isAny: false,
                          conditions: [],
                        }
                  }
                  onChange={(nextValue) => {
                    onChange?.(nextValue)
                    // Show/hide conditions based on whether there are any conditions
                    if (nextValue && nextValue.conditions && nextValue.conditions.length > 0) {
                      setShowConditions(true)
                      // Update lastShowOnlyWhenConditions when conditions change (but not for always-hidden pattern)
                      if (!isAlwaysHiddenPattern(nextValue)) {
                        setLastShowOnlyWhenConditions(nextValue)
                      }
                    } else {
                      setShowConditions(false)
                      // Clear lastShowOnlyWhenConditions when all conditions are removed
                      setLastShowOnlyWhenConditions(null)
                    }
                  }}
                  parentFormId={parentFormId}
                />
              ) : (
                /* Add Condition Button - Text button */
                <button
                  onClick={() => {
                    setShowConditions(true)
                    // Add default condition when button is clicked
                    onChange?.({
                      isAny: false,
                      conditions: getDefaultCondition(),
                    })
                  }}
                  className='text-teal-500 hover:text-teal-600 dark:text-teal-400 dark:hover:text-teal-300 font-medium text-sm transition-colors flex items-center gap-2'
                >
                  <MaterialIcon icon='add' size={16} />
                  <Trans>Add condition</Trans>
                </button>
              )}
            </div>
          )}
        </div>

        {/* Always Hidden */}
        <button
          onClick={() => handleModeChange('always-hidden')}
          className={`flex items-center gap-3 w-full p-4 rounded-lg border-2 transition-all ${
            currentMode === 'always-hidden'
              ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
              : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'
          }`}
        >
          <div
            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
              currentMode === 'always-hidden' ? 'border-teal-500 bg-teal-500' : 'border-gray-300 dark:border-gray-600'
            }`}
          >
            {currentMode === 'always-hidden' && <div className='w-2 h-2 rounded-full bg-white' />}
          </div>
          <div
            className={`flex-shrink-0 ${
              currentMode === 'always-hidden' ? 'text-teal-500' : 'text-gray-400 dark:text-gray-500'
            }`}
          >
            <EyeSlash24 width={20} height={20} />
          </div>
          <span
            className={`font-semibold flex-1 text-left ${
              currentMode === 'always-hidden' ? 'text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'
            }`}
          >
            <Trans>Always hidden</Trans>
          </span>
        </button>
      </div>
    </div>
  )
}
